import React from "react";
import CompetitionTermsPage from "../pages/CompetitionTermsPage";
import RulesDisclaimer from "./RulesDisclaimer";
import { useLanguage } from "../i18n/LanguageProvider";

const darkSection = {
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.16)",
  background: "rgba(255,255,255,0.06)",
  padding: 12,
  display: "grid",
  gap: 10,
};

const lightSection = {
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.08)",
  background: "rgba(0,0,0,0.05)",
  padding: 12,
  display: "grid",
  gap: 10,
};

function RuleRow({ rule, variant = "dark" }) {
  const light = variant === "light";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 4,
        minWidth: 0,
        padding: "9px 10px",
        borderRadius: 12,
        border: light ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.10)",
        background: light
          ? "rgba(255,255,255,0.62)"
          : "linear-gradient(135deg, rgba(255,255,255,0.075), rgba(255,255,255,0.035))",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          color: light ? "rgba(0,0,0,0.62)" : "rgba(255,255,255,0.66)",
          fontWeight: 1000,
          fontSize: 10,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          lineHeight: 1.15,
        }}
      >
        {rule.label}
      </div>
      <div
        style={{
          color: light ? "#000" : "white",
          fontWeight: 1000,
          fontSize: 14,
          letterSpacing: 0.3,
          textTransform: "none",
          lineHeight: 1.3,
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          opacity: light ? 0.9 : 1,
        }}
      >
        {rule.value}
      </div>
    </div>
  );
}

function CompetitionTermsNotice({ variant, onOpenCompetitionTerms }) {
  const { t } = useLanguage();
  const light = variant === "light";
  return (
    <div style={light ? lightSection : darkSection}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 1000,
          letterSpacing: 0.7,
          textTransform: "uppercase",
          color: light ? "#000" : "#fff",
        }}
      >
        {t("competitionRules.competitionTermsTitle")}
      </div>
      <div
        style={{
          color: light ? "rgba(0,0,0,0.78)" : "rgba(255,255,255,0.78)",
          fontSize: 13,
          lineHeight: 1.4,
          fontWeight: 800,
        }}
      >
        {t("competitionRules.competitionTermsText")}
      </div>
      {typeof onOpenCompetitionTerms === "function" && (
        <button
          type="button"
          onClick={onOpenCompetitionTerms}
          style={{
            minHeight: 42,
            border: light ? "1px solid rgba(0,0,0,0.16)" : "1px solid rgba(255,255,255,0.18)",
            borderRadius: 12,
            background: light
              ? "rgba(255,255,255,0.7)"
              : "linear-gradient(135deg, rgba(255,0,127,0.86), rgba(122,43,255,0.86))",
            color: light ? "#000" : "#fff",
            fontWeight: 1000,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            cursor: "pointer",
          }}
        >
          {t("competitionRules.openCompetitionTerms")}
        </button>
      )}
    </div>
  );
}

export default function CompetitionRulesView({
  coreRules = [],
  creatorRules = [],
  title = null,
  variant = "dark",
  showCompetitionTerms = false,
  onOpenCompetitionTerms = null,
  onBackFromCompetitionTerms = null,
}) {
  const { t } = useLanguage();
  const light = variant === "light";
  const rules = [...coreRules, ...creatorRules];

  if (showCompetitionTerms) {
    return (
      <div
        style={{
          borderRadius: 18,
          background: "linear-gradient(145deg, rgba(6,2,35,0.98), rgba(18,8,66,0.98))",
          border: "1px solid rgba(255,255,255,0.14)",
          padding: 14,
          boxSizing: "border-box",
          color: "#fff",
          minWidth: 0,
        }}
      >
        <CompetitionTermsPage
          showBackButton={true}
          onBack={onBackFromCompetitionTerms}
          embedded={true}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={light ? lightSection : { display: "grid", gap: 16 }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: light ? 17 : 18,
            color: light ? "#000" : "#fff",
          }}
        >
          {title || t("competitionRules.tournamentRulesTitle")}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {rules.map((rule, index) => (
            <RuleRow key={rule.key || `${rule.label}-${rule.value}-${index}`} rule={rule} variant={variant} />
          ))}
        </div>
      </div>

      <CompetitionTermsNotice
        variant={variant}
        onOpenCompetitionTerms={onOpenCompetitionTerms}
      />
      <RulesDisclaimer variant={variant} />
    </div>
  );
}
